import { Checkbox, Input, InputGroup, InputRightAddon } from '@chakra-ui/react';
import { filter, map } from 'lodash';
import { FC } from 'react';

import {
  AccessorialDescription,
  AllCommonAccessorials,
  AllEndpointAccessorials,
  CommonAccessorialsDescription,
  EndPointAccessorialsDescription,
} from '@/utils/Accessories';
import { BasicCommonAccessorials, EndPointAccessorials } from '@/utils/types';

import ValidatedField from '../ValidatedField';

import LabelText from './LabelText';

type AccessorialListProps<T extends Record<string, boolean>> = {
  accessorialsDescription: AccessorialDescription<T>;
  name: string;
  allAccessorials: (keyof T)[];
  isDisabled?: (name: string) => boolean;
};

const AccessorialList: FC<
  | AccessorialListProps<BasicCommonAccessorials>
  | AccessorialListProps<EndPointAccessorials>
> = ({ accessorialsDescription, name, allAccessorials, isDisabled }) => (
  <>
    {map(
      filter(
        allAccessorials,
        accessoryName => accessoryName in accessorialsDescription,
      ),
      (accessoryName: string) => (
        <div key={accessoryName} className="mb-2 md:mr-4">
          <ValidatedField name={`${name}.${accessoryName}`}>
            {({ field: { value, name, ...field } }) => (
              <div className="flex flex-row">
                <div>
                  <Checkbox
                    name={name}
                    {...field}
                    value={value || false}
                    isDisabled={isDisabled && isDisabled(name)}
                    isChecked={value}
                    colorScheme="green"></Checkbox>
                </div>
                <div className="ml-1 leading-none">
                  {/* @ts-expect-error TODO Couldn't get to some of these in https://plslogistics.atlassian.net/browse/GS-736 */}
                  <div>{accessorialsDescription[accessoryName].label}</div>
                  {/* @ts-expect-error TODO Couldn't get to some of these in https://plslogistics.atlassian.net/browse/GS-736 */}
                  {accessorialsDescription[accessoryName].explanation && (
                    <div className="text-xs mt-1">
                      {/* @ts-expect-error TODO Couldn't get to some of these in https://plslogistics.atlassian.net/browse/GS-736 */}
                      {accessorialsDescription[accessoryName].explanation}
                    </div>
                  )}
                </div>
              </div>
            )}
          </ValidatedField>
        </div>
      ),
    )}
  </>
);

const AdditionalServices: FC<{
  name: string;
  label?: string;
  endpointField: string;
  endPointAccessorials?: EndPointAccessorialsDescription;
  commonAccessorials?: CommonAccessorialsDescription;
  temperature?: boolean;
  isDisabled?: (name: string) => boolean;
}> = ({
  name,
  label,
  endpointField,
  endPointAccessorials,
  commonAccessorials,
  temperature,
  isDisabled,
}) => (
  <div>
    {label && (
      <div className="mb-2 flex items-center">
        <div className="mr-1">
          <LabelText>{label}</LabelText>
        </div>
      </div>
    )}
    <div className="flex flex-col md:flex-row">
      {endPointAccessorials && (
        <AccessorialList
          name={`${name}.${endpointField}.accessorials`}
          accessorialsDescription={endPointAccessorials}
          allAccessorials={AllEndpointAccessorials}
          isDisabled={isDisabled}
        />
      )}

      {commonAccessorials && (
        <AccessorialList
          name={`${name}.commonAccessorials`}
          accessorialsDescription={commonAccessorials}
          allAccessorials={AllCommonAccessorials}
        />
      )}

      {temperature && (
        <div key="temperature" className="mb-2 md:mr-4">
          <div className="mb-2 md:w-40">
            <ValidatedField
              label="Temperature"
              name={`${name}.commonAccessorials.temperature`}>
              {({ field }) => (
                <InputGroup>
                  <Input
                    type="number"
                    max={100}
                    min={-100}
                    maxLength={5}
                    {...field}
                  />
                  <InputRightAddon className="flex justify-end">
                    °F
                  </InputRightAddon>
                </InputGroup>
              )}
            </ValidatedField>
          </div>
        </div>
      )}
    </div>
  </div>
);

export default AdditionalServices;
